/**
 * ============================================================
 * FILE: core/di/config.js – DI-Container Registrierung (VOLLSTÄNDIG)
 * ============================================================
 */

import SettingsManager from '../settings.js';
import SaveManager from '../persistence/save-manager.js';
import CloudManager from '../persistence/cloud-manager.js';

// Services
import HeroService from '../services/hero-service.js';
import ResourceService from '../services/resource-service.js';
import ClanService from '../services/clan-service.js';
import StoryService from '../services/story-service.js';
import ForgeService from '../services/forge-service.js';
import CraftingService from '../services/crafting-service.js';
import QuestService from '../services/quest-service.js';
import AchievementService from '../services/achievement-service.js';
import FriendService from '../services/friend-service.js';
import ChatService from '../services/chat-service.js';
import CodexService from '../services/codex-service.js';
import RelicHuntService from '../services/relic-hunt-service.js';
import DailyRewardService from '../services/daily-reward-service.js';
import LeaderboardService from '../services/leaderboard-service.js';
import StoryBranchService from '../services/story-branch-service.js';
import ChallengeService from '../services/challenge-service.js';
import LibraryService from '../services/library-service.js';
import TutorialService from '../services/tutorial-service.js';
import NetworkService from '../services/network-service.js';
import I18nService from '../services/i18n-service.js';
import TalentService from '../services/talent-service.js';
import CombatAnalyticsService from '../services/combat-analytics-service.js';
import AuthService from '../services/auth-service.js';
import AccountVaultService from '../services/account-vault-service.js';
import IdleService from '../services/idle-service.js';

/**
 * Registriert ALLE Dienste im DI-Container.
 */
export function registerServices(container) {
  // ============================================================
  // CORE
  // ============================================================
  
  container.register('settingsManager', (c) => new SettingsManager(c.get('eventBus'), c.get('stateManager')));
  container.register('i18nService', (c) => new I18nService(c.get('eventBus'), c.get('settingsManager')));
  container.register('authService', (c) => new AuthService(
    c.get('eventBus'),
    c.get('settingsManager'),
    c.get('networkService'),
    c.get('cloudManager')
  ));
  container.register('accountVaultService', (c) => new AccountVaultService(c.get('eventBus'), c.get('authService')));
  container.register('networkService', (c) => new NetworkService(
    c.get('stateManager'),
    c.get('eventBus'),
    () => c.get('chatService'),
    () => c.get('leaderboardService'),
    () => c.get('cloudManager'),
    () => c.get('authService')
  ));
  container.register('cloudManager', (c) => new CloudManager(c.get('eventBus'), c.get('networkService')));

  // ============================================================
  // SERVICES
  // ============================================================
  
  container.register('heroService', (c) => new HeroService(c.get('stateManager'), c.get('eventBus')));
  container.register('resourceService', (c) => new ResourceService(c.get('stateManager'), c.get('eventBus'), c.get('accountVaultService')));
  container.register('clanService', (c) => new ClanService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService')));
  container.register('storyService', (c) => new StoryService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService'), c.get('heroService')));
  container.register('forgeService', (c) => new ForgeService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService'), c.get('heroService')));
  container.register('craftingService', (c) => new CraftingService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService'), c.get('heroService'), c.get('clanService'), c.get('forgeService')));
  container.register('questService', (c) => new QuestService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService'), c.get('heroService'), c.get('clanService')));
  container.register('achievementService', (c) => new AchievementService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService'), c.get('heroService')));
  container.register('friendService', (c) => new FriendService(c.get('stateManager'), c.get('eventBus'), c.get('heroService')));
  container.register('chatService', (c) => new ChatService(c.get('stateManager'), c.get('eventBus'), c.get('heroService'), c.get('clanService')));
  container.register('codexService', (c) => new CodexService(c.get('stateManager'), c.get('eventBus'), c.get('heroService')));
  container.register('relicHuntService', (c) => new RelicHuntService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService'), c.get('heroService')));
  container.register('dailyRewardService', (c) => new DailyRewardService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService'), c.get('heroService')));
  container.register('leaderboardService', (c) => new LeaderboardService(c.get('stateManager'), c.get('eventBus'), c.get('networkService')));
  container.register('storyBranchService', (c) => new StoryBranchService(c.get('stateManager'), c.get('eventBus'), c.get('heroService')));
  container.register('talentService', (c) => new TalentService(c.get('stateManager'), c.get('eventBus')));
  container.register('combatAnalyticsService', (c) => new CombatAnalyticsService(c.get('eventBus')));
  container.register('challengeService', (c) => new ChallengeService(c.get('stateManager'), c.get('eventBus'), c.get('heroService')));
  container.register('libraryService', (c) => new LibraryService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService')));
  container.register('tutorialService', (c) => new TutorialService(c.get('stateManager'), c.get('eventBus')));
  container.register('idleService', (c) => new IdleService(c.get('stateManager'), c.get('eventBus'), c.get('resourceService')));

  // ============================================================
  // PERSISTENZ
  // ============================================================
  
  container.register('saveManager', (c) => {
    SaveManager.setServices({
      stateManager: c.get('stateManager'),
      authService: c.get('authService'),
      heroService: c.get('heroService'),
      resourceService: c.get('resourceService'),
      clanService: c.get('clanService'),
      storyService: c.get('storyService'),
      forgeService: c.get('forgeService'),
      craftingService: c.get('craftingService'),
      questService: c.get('questService'),
      achievementService: c.get('achievementService'),
      friendService: c.get('friendService'),
      chatService: c.get('chatService'),
      codexService: c.get('codexService'),
      relicHuntService: c.get('relicHuntService'),
      dailyRewardService: c.get('dailyRewardService'),
      leaderboardService: c.get('leaderboardService'),
      storyBranchService: c.get('storyBranchService'),
      challengeService: c.get('challengeService'),
      libraryService: c.get('libraryService'),
      idleService: c.get('idleService'),
      cloudManager: c.get('cloudManager')
    });
    return SaveManager;
  });

  // ============================================================
  // GAME LOOP
  // ============================================================
  
  // HINWEIS: "gameLoop" wird bewusst NICHT hier registriert.
  // game-boot.js erstellt die GameLoop-Instanz erst NACH der State-Initialisierung
  // (Abschnitt 6) und registriert sie dort selbst. Eine Registrierung hier
  // würde nur eine ungenutzte Zweit-Instanz erzeugen und die Warnung
  // "[DI] Dienst 'gameLoop' wird überschrieben." auslösen.
}

export default registerServices;
